<template>
    <div :class="{ 'color-selector': true, [`color-selector--${variant}`]: variant }">
        <div class="color-selector__wrapper">
            <component
                :is="isSwipeable ? 'dk-slider' : 'div'"
                :ref="isSwipeable ? 'dkSlider' : null"
                :swiper-opts="isSwipeable ? swiperOptions : false"
                :options="isSwipeable ? swiperOpts : false"
                class="color-selector__items"
                @clicked-slide="onItemClick"
            >
                <component
                    :is="isSwipeable ? 'dk-slide' : 'div'"
                    v-for="(item, i) in itemList"
                    v-show="i < maxItems || expanded || isSwipeable"
                    :key="i"
                    :class="getItemClasses(!isSwipeable, item.active)"
                    @click="onItemClick(i)"
                >
                    <figure v-if="isSideView || isSwipeable" :class="getItemClasses(isSwipeable, item.active)">
                        <deferred-img
                            class="color-selector__image"
                            :src="item.colorImage && item.colorImage.image200"
                            :alt="item.description"
                            :title="item.description"
                            @error="onImageError(i)"
                        />
                    </figure>
                    <div v-else class="color-selector__swatch" :style="{ backgroundColor: item.primaryHex }">
                        <div
                            class="color-selector__swatch-secondary"
                            :style="{ backgroundColor: item.secondaryHex }"
                        ></div>
                    </div>
                </component>
                <div v-if="togglerVisible" class="color-selector__item" @click="onTogglerClick">
                    <icon name="generic-details" size="small" color="grey-600" />
                </div>
            </component>
        </div>
    </div>
</template>

<script>
/**
 * A component for displaying a color selector
 *
 * @version 4.0.0
 */
export default {
    name: 'ColorSelector',
    components: {
        Icon: () => ({
            component: import(/* webpackChunkName: "Icon-vue" */ '@v4/common/components/atoms/Icon'),
        }),
        DeferredImg: () => ({
            component: import(/* webpackChunkName: "DeferredImg-vue" */ '@v4/common/components/atoms/DeferredImg'),
        }),
        DkSlide: () => ({
            component: import(/* webpackChunkName: "DkSlide-vue" */ '@v4/common/components/atoms/DkSlide'),
        }),
        DkSlider: () => ({
            component: import(/* webpackChunkName: "DkSlider-vue" */ '@v4/common/components/molecules/DkSlider'),
        }),
    },
    props: {
        /**
         * UI variants. Available variants: `swipeable` and `side-view`
         */
        variant: {
            type: String,
            default: '',
            validator(value) {
                return ['', 'swipeable', 'side-view'].includes(value);
            },
        },
        /**
         * Item list
         */
        items: {
            type: Array,
            default: () => [],
        },
        /**
         * Number of items visible
         */
        maxItems: {
            type: Number,
            default: 5,
        },
    },
    data() {
        return {
            itemList: [],
            expanded: false,
            swiperOpts: {
                pagination: false,
                navigation: this.hasHiddenItems,
            },
            swiperOptions: {
                slidesPerView: this.maxItems + 0.5,
                spaceBetween: 15,
                breakpoints: {
                    767: {
                        slidesPerView: this.maxItems + 3.5,
                    },
                    639: {
                        slidesPerView: this.maxItems + 1.5,
                    },
                    511: {
                        slidesPerView: this.maxItems - 0.5,
                    },
                    374: {
                        slidesPerView: this.maxItems - 1.5,
                    },
                },
            },
        };
    },
    computed: {
        isSideView() {
            return this.variant === 'side-view';
        },
        isSwipeable() {
            return this.variant === 'swipeable';
        },
        hasHiddenItems() {
            return this.itemList.length - this.maxItems > 0;
        },
        togglerVisible() {
            return !this.isSwipeable && !this.expanded && this.hasHiddenItems;
        },
    },
    created() {
        this.itemList = this.items;
    },
    methods: {
        getItemClasses(bool, active) {
            return {
                'color-selector__item': bool,
                'color-selector__item--active': bool && active,
            };
        },
        onImageError(index) {
            this.itemList = this.itemList.filter((item, i) => i !== index);
        },
        onTogglerClick() {
            this.expanded = !this.expanded;
        },
        slideByIndex(index = 0) {
            if (
                !this.$refs.dkSlider ||
                !this.$refs.dkSlider.swiper ||
                typeof this.$refs.dkSlider.swiper.slideTo !== 'function'
            ) {
                return;
            }

            this.$refs.dkSlider.swiper.slideTo(index);
        },
        onItemClick(event) {
            const index = typeof event === 'object' ? event.clickedIndex : event;
            const selectedItem = this.itemList[index];
            const currentActiveIndex = this.itemList.findIndex((item) => item.active);

            if (!selectedItem || index === currentActiveIndex) {
                return;
            }

            this.itemList = this.itemList.map((item) => ({ ...item, active: false }));
            this.$set(this.itemList, index, { ...selectedItem, active: true });
            this.slideByIndex(index);

            /**
             * Fired when clicked on an item
             *
             * @event item-click
             * @type {number}
             * @property {number} index item's index
             */
            this.$emit('item-click', index);
        },
    },
};
</script>

<style lang="scss" src="@v4/common/components/molecules/ColorSelector/_index.scss"></style>
